import { createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/fp/cloneDeep';
import flow from 'lodash/fp/flow';
import forEach from 'lodash/fp/forEach';
import has from 'lodash/fp/has';
import keys from 'lodash/fp/keys';

import { DEFAULT_LOCALE, extractLanguage, supportedLocaleMap } from './lang';
import messagesEN from './translations/performanceAnalysis_en-GB.json';

export type LangState = {
    availableLocales: {
        [key: string]: string;
    };
    allMessages: {
        [key: string]: {
            [key: string]: string;
        };
    };
    canFetchSupportedLocale: boolean;
    displayLocale: string;
    displayMessages: {
        [key: string]: string;
    };
    preferredLocale: string;
    supportedLocale: string;
};

const defaultMessages: { [key: string]: { [key: string]: string } } = {
    en: messagesEN,
    [DEFAULT_LOCALE]: messagesEN,
};

const getSupportedLocaleFromData = (data: { [key: string]: string | { [key: string]: string } }) =>
    flow(
        (locale) => (has(locale, data) ? locale : extractLanguage(locale)),
        (locale) => (has(locale, data) ? locale : DEFAULT_LOCALE),
    );

const getMessages = (
    { allMessages }: { allMessages: { [key: string]: { [key: string]: string } } },
    locale: string,
): { [key: string]: string } =>
    allMessages[supportedLocaleMap[locale]] || allMessages[DEFAULT_LOCALE];

const isLangOnly = (locale: string) => !/-/.test(locale);

const defaultFor = (locale: string): string => supportedLocaleMap[locale];

const patchMissingMessages = (
    knownMessages: { [key: string]: string },
    messages: { [key: string]: string },
) => {
    const result = cloneDeep(messages);

    flow(
        keys,
        forEach((key: string) => {
            if (!has(key, result)) {
                result[key] = knownMessages[key];
            }
        }),
    )(knownMessages);

    return result;
};

const applyLocale = (
    state: {
        allMessages: { [key: string]: { [key: string]: string } };
        availableLocales: { [key: string]: string };
    },
    preferredLocale: string,
) => {
    const { allMessages } = state;

    const getDisplayLocale = getSupportedLocaleFromData(allMessages);
    const getSupported = getSupportedLocaleFromData(supportedLocaleMap);

    const displayLocale = getDisplayLocale(preferredLocale);

    const closest = getSupported(preferredLocale);
    const supportedLocale = isLangOnly(closest) ? defaultFor(closest) : closest;

    const canFetchSupportedLocale = displayLocale !== supportedLocale;

    const displayMessages = patchMissingMessages(
        getMessages(state, DEFAULT_LOCALE),
        getMessages(state, displayLocale),
    );

    const result = {
        availableLocales: state.availableLocales,
        allMessages,
        canFetchSupportedLocale,
        displayLocale,
        displayMessages,
        preferredLocale,
        supportedLocale,
    };

    return result;
};

export const defaultState = applyLocale(
    { allMessages: defaultMessages, availableLocales: supportedLocaleMap },
    DEFAULT_LOCALE,
);

const isDefaultLocaleForLang = (locale: string) => supportedLocaleMap[locale] === locale;

const hasLocale = (locale: string) => Boolean(supportedLocaleMap[locale]);

const mergeLanguageData = (
    allMessages: { [key: string]: { [key: string]: string } },
    languageData: { [key: string]: string },
    locale: string,
) => {
    const baseLang = extractLanguage(locale);
    const messages = {
        [locale]: languageData,
    };

    if (baseLang && (isDefaultLocaleForLang(locale) || !hasLocale(baseLang))) {
        messages[baseLang] = languageData;
    }

    return {
        ...allMessages,
        ...messages,
    };
};

const langSlice = createSlice({
    name: 'lang',
    initialState: defaultState,
    reducers: {
        changeLocale(state, { payload }) {
            return { ...state, ...applyLocale(state, payload) };
        },
        languageDataFetched(state, { payload }) {
            const { locale, languageData } = payload;
            if (!languageData) {
                return {
                    ...state,
                    ...applyLocale(state, locale),
                };
            }

            const merged = {
                ...state,
                allMessages: mergeLanguageData(state.allMessages, languageData, locale),
            };

            return {
                ...merged,
                ...applyLocale(merged, locale),
            };
        },
    },
});

export const { changeLocale, languageDataFetched } = langSlice.actions;

export default langSlice.reducer;
